import { Button, Progress, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { RestrictionType } from "api/resources/learningItems/types"
import CpatCard from "assets/Cpat/cpat-card.webp"
import Monitor from "assets/Fallback/monitor.svg"
import FallbackImage from "assets/Placeholder/product-image-placeholder.svg"
import clsx from "clsx"
import Fallback from "components/Fallback"
import FullPageLoading from "components/FullPageLoading"
import PageContainer from "components/PageContainer"
import { getRestrictionIcon } from "features/AccessManagement/CheckRestrictions"
import CpatBanner from "features/Cpat/Banners/CpatHomeScreenBanner"
import { ResumeLearningCard } from "features/Home/ResumeLearning"
import { IMPACT_COURSE_TOUR_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/ImpactCourseTour/constants"
import ResumeImpactCourseTour from "features/Home/SuraasaPlusNewYearOnboarding/ImpactCourseTour/ResumeImpactCourseTour"
import { useImpactCourseTour } from "features/Home/SuraasaPlusNewYearOnboarding/ImpactCourseTour/useImpactCourseTour"
import {
  getDeliveryModeBadge,
  getProductImage,
} from "features/LearningItems/getProductDisplayConfig"
import FreeTrialBanner from "features/SuraasaPlus/FreeTrial/Banner"
import { context } from "global/Context/context"
import { Message } from "iconoir-react"
import { useContext } from "react"
import { Link } from "react-router-dom"
import routes from "routes"
import { learningItemSlugs } from "utils/constants"
import { generateModuleAccessRequestLink } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import ItemList from "../../components/ItemList"
import ItemRow from "../../components/ItemRow"
import TopHeadingBar from "../../components/TopHeadingBar"
import { getPercentage } from "../Library/CourseInfoAndProgress"
import GetStartedBannerForCourses from "./GetStartedBannerForCourses"

const Library = () => {
  const { learningRestrictions, isNcte } = useContext(context)

  const { pgctl, cpat } = useEnrollments()

  const { data, isLoading, isSuccess, isError } = useQuery({
    queryFn: () => api.learningItems.getPlannerList(),
    queryKey: queries.learningItems.listPlanners().queryKey,
  })
  useImpactCourseTour({ isLoading, page: "learning" })

  const { data: lastAccessedItem, isSuccess: isLastItemSuccess } = useQuery({
    queryFn: () => api.learningItems.getLastAccessedItem(),
    queryKey: ["last-accessed-item"],
  })

  const shouldShowLastAccessedItem = data && data.length > 1 && lastAccessedItem

  const showCPATPrimaryCard =
    cpat.hasCpat && data?.length === 0 ? true : !cpat.hasCompletedCpat
  const showCPATSecondaryCard = cpat.hasCpat && !showCPATPrimaryCard

  return (
    <div className="relative">
      <FreeTrialBanner />
      <TopHeadingBar
        heading="Learning"
        renderBorder={(data?.length ?? 0) > 0}
        endSlot={
          <Link to={routes.discussions}>
            <Button startAdornment={<Message />} variant="text" color="black">
              Discussions
            </Button>
          </Link>
        }
      />
      <PageContainer className="mt-2.5 h-full pb-20 sm:pb-0">
        {isLoading && <FullPageLoading />}
        {isSuccess && (
          <>
            {showCPATPrimaryCard && <CpatBanner />}

            {isLastItemSuccess && shouldShowLastAccessedItem && (
              <ResumeLearningCard
                className="mb-2"
                item={lastAccessedItem}
                progress={lastAccessedItem.progress}
              />
            )}

            {data.length > 0 && (
              <ItemList className="mb-4 overflow-hidden !py-0 sm:mb-[45px] md:mb-6">
                {showCPATSecondaryCard && (
                  <ItemRow
                    to={routes.cpat.dashboard}
                    infoSlot={
                      <div className="flex w-full flex-wrap items-center justify-between gap-2.5 p-1 md:flex-row">
                        <div className="flex flex-row gap-2">
                          <img
                            alt="course"
                            src={CpatCard}
                            className="size-[73px] shrink-0 overflow-hidden rounded-xl border border-surface-100 object-cover"
                            width={130}
                          />

                          <div className="flex flex-col">
                            <Typography className="mb-1 text-preTitle !tracking-normal text-muted">
                              <span className="uppercase">assessment</span>
                            </Typography>
                            <Typography variant="title3" className="text-black">
                              {isNcte
                                ? "National Council for Teacher Education Assessment"
                                : "Comprehensive Pedagogy Assessment of Teachers"}
                            </Typography>
                          </div>
                        </div>

                        <Link
                          to={routes.cpat.dashboard}
                          className="w-full sm:w-fit"
                        >
                          <Button
                            color="black"
                            className="w-full border border-onSurface-300 text-white sm:w-fit"
                            size="sm"
                          >
                            Open Dashboard
                          </Button>
                        </Link>
                      </div>
                    }
                  />
                )}
                {...data
                  .filter(item => {
                    const restrictionType = learningRestrictions?.find(
                      i => i.learningItem.slug === item.slug
                    )?.restrictionType

                    if (shouldShowLastAccessedItem && !restrictionType)
                      return (
                        item.learningItemId !== lastAccessedItem.learningItemId
                      )
                    return true
                  })
                  .map(item => {
                    const restrictionType = learningRestrictions?.find(
                      i => i.learningItem.slug === item.slug
                    )?.restrictionType

                    const currentItemNumber = item.progress
                      ? item.progress.completedUserPlannerItems
                      : 0
                    const totalUserPlannerItems = item.progress
                      ? item.progress.totalUserPlannerItems
                      : 0

                    const percentage = getPercentage({
                      total: totalUserPlannerItems,
                      completed: currentItemNumber,
                    })

                    const isPGCTL = item.slug === learningItemSlugs.pgctl

                    const isCompleted = percentage === 100

                    const isInProgress =
                      !isCompleted &&
                      (item.progress
                        ? item.progress.completedUserPlannerItems > 0
                        : false)

                    const badge = isPGCTL
                      ? getDeliveryModeBadge({
                          deliveryMode: pgctl.deliveryMode,
                        })
                      : null

                    return (
                      <ItemRow
                        key={item.uuid}
                        component={
                          restrictionType !== RestrictionType.REVOKED
                            ? Link
                            : undefined
                        }
                        onClick={() => {
                          trackingService.trackEvent(
                            TrackingEvents.libraryItemClicked,
                            {
                              slug: item.slug,
                            }
                          )
                        }}
                        to={
                          restrictionType !== RestrictionType.REVOKED
                            ? `/${item.learningItemType}/${item.slug}/overview/`
                            : undefined
                        }
                        className={clsx({
                          [IMPACT_COURSE_TOUR_TARGETS.impactCourse]:
                            item.slug === learningItemSlugs.impactCourse,
                        })}
                        disabled={!!restrictionType}
                        infoSlot={
                          <div className="flex grow flex-col items-start p-1">
                            <div className="flex w-full items-center justify-between">
                              <div className="flex">
                                <div className="relative me-2 size-[73px] shrink-0 overflow-hidden rounded-xl">
                                  {restrictionType && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-onSurface-800/50 text-white">
                                      <div className="flex size-5 items-center justify-center rounded-lg bg-onSurface-900/80">
                                        {getRestrictionIcon(restrictionType)}
                                      </div>
                                    </div>
                                  )}

                                  <img
                                    alt="course"
                                    onError={({ currentTarget }) => {
                                      currentTarget.onerror = null
                                      currentTarget.src = FallbackImage
                                    }}
                                    src={getProductImage({
                                      slug: item.slug,
                                      defaultImage: item.image,
                                      deliveryMode: pgctl.deliveryMode,
                                    })}
                                    className="size-full rounded-xl border border-surface-100 object-cover"
                                  />
                                </div>

                                <div className="flex flex-col">
                                  <Typography className="mb-1 text-preTitle !tracking-normal text-muted">
                                    <span className="uppercase">
                                      {item.learningItemType}
                                    </span>
                                    {badge && (
                                      <>
                                        <span className="text-onSurface-300">
                                          {" • "}
                                        </span>
                                        <span className={badge.color}>
                                          {badge.text}
                                        </span>
                                      </>
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="title3"
                                    className="text-black"
                                  >
                                    {item.name}
                                  </Typography>
                                  <div className="mt-2 hidden items-center gap-1 sm:flex">
                                    <Progress
                                      value={percentage}
                                      className={clsx("mb-1 w-15", {
                                        "bg-surface-100": restrictionType,
                                      })}
                                      indicatorClassName={clsx({
                                        "!bg-onSurface-500": restrictionType,
                                        "bg-success-500": isCompleted,
                                      })}
                                    />
                                    <Typography className="mb-0.5 text-strongSmallBody text-onSurface-800 sm:mb-1">
                                      {percentage}%{" "}
                                      <span className="text-smallBody text-muted">
                                        Complete
                                      </span>
                                    </Typography>
                                  </div>
                                </div>
                              </div>

                              <div
                                className={clsx("hidden sm:block", {
                                  [IMPACT_COURSE_TOUR_TARGETS.learningButtonWeb]:
                                    item.slug ===
                                    learningItemSlugs.impactCourse,
                                })}
                              >
                                {getActionButton({
                                  slug: item.slug,
                                  restrictionType,
                                  name: item.name,
                                  isCompleted,
                                  isInProgress,
                                })}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-1 sm:hidden">
                              <Progress
                                value={percentage}
                                className={clsx("mb-1 w-15", {
                                  "bg-onSurface-100": restrictionType,
                                })}
                                indicatorClassName={clsx({
                                  "!bg-onSurface-500": restrictionType,
                                  "bg-success-500": isCompleted,
                                })}
                              />
                              <Typography className="mb-0.5 text-strongSmallBody text-onSurface-800 sm:mb-1">
                                {percentage}%{" "}
                                <span className="text-smallBody text-muted">
                                  Complete
                                </span>
                              </Typography>
                            </div>
                            <div
                              className={clsx("mt-3 w-full sm:hidden", {
                                [IMPACT_COURSE_TOUR_TARGETS.learningButtonMobile]:
                                  item.slug === learningItemSlugs.impactCourse,
                              })}
                            >
                              {getActionButton({
                                slug: item.slug,
                                restrictionType,
                                name: item.name,
                                isCompleted,
                                isInProgress,
                              })}
                            </div>
                          </div>
                        }
                      />
                    )
                  })}
              </ItemList>
            )}
          </>
        )}

        {data?.length === 0 && <GetStartedBannerForCourses />}

        {isError && (
          <Fallback
            data={{
              title: "Something went wrong!",
              description: "Please contact care@suraasa.com",
              image: Monitor,
            }}
            className="my-6 px-2 sm:my-20 sm:px-0 md:my-8"
          />
        )}
      </PageContainer>
      <ResumeImpactCourseTour />
    </div>
  )
}

export default Library

interface ActionButtonProps {
  restrictionType: RestrictionType | undefined
  name: string
  isCompleted: boolean
  isInProgress?: boolean
  slug: string
}

const getActionButton = ({
  restrictionType,
  name,
  isCompleted,
  isInProgress,
}: ActionButtonProps) => {
  return restrictionType === RestrictionType.REVOKED ? (
    <Link
      to={generateModuleAccessRequestLink({
        modules: [name],
        type: restrictionType,
      })}
    >
      <Button
        color={null}
        className="w-full border border-onSurface-300 text-onSurface-700 sm:w-fit"
        size="sm"
      >
        Contact Us
      </Button>
    </Link>
  ) : (
    <Button
      color={null}
      className={clsx(
        "w-full border border-onSurface-300 text-onSurface-700 sm:w-fit"
      )}
      size="sm"
    >
      {isCompleted ? "View" : isInProgress ? "Continue" : "Start Learning"}
    </Button>
  )
}
